import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

/**
 * Particle model for a single emitted lily
 */
interface LillyParticle {
    id: number;
    /** Starting X position in viewport coordinates */
    startX: number;
    /** Starting Y position in viewport coordinates */
    startY: number;
    /** Velocity in pixels per second (X direction) */
    velocityX: number;
    /** Velocity in pixels per second (Y direction) */
    velocityY: number;
    /** Rotation speed in degrees per second */
    rotationSpeed: number;
    /** Particle lifetime in milliseconds */
    lifetime: number;
    /** Timestamp when particle was created */
    createdAt: number;
}

/**
 * LillyEmitter props
 */
export interface LillyEmitterProps {
    /** Whether the emitter is actively spawning particles */
    isEmitting: boolean;
    /** Origin point in viewport coordinates (center of the Lilly item) */
    origin: { x: number; y: number } | null;
    /** Source image for the lily particles */
    src: string;
    /** Width of each particle in pixels */
    particleWidth: number;
}

// Emitter configuration constants
const SPAWN_INTERVAL_MS = 90; // Spawn a particle every 90ms for a quick burst
const MAX_PARTICLES = 32; // Maximum concurrent particles
const PARTICLE_LIFETIME_MIN = 1500; // Minimum lifetime in ms - longer to fly across page
const PARTICLE_LIFETIME_MAX = 2500; // Maximum lifetime in ms - longer for floaty feel and cross-page travel
const VELOCITY_MIN = 100; // Minimum velocity in px/s - slower for gentler burst
const VELOCITY_MAX = 240; // Maximum velocity in px/s
const ROTATION_SPEED_MIN = 40; // Min rotation speed in deg/s - slower rotation
const ROTATION_SPEED_MAX = 120; // Max rotation speed in deg/s

/**
 * Generate random velocity vector for radial burst effect within 270° arc
 * Arc spans from -45° to 225° (forward-facing spew pattern)
 */
function generateVelocity(): { vx: number; vy: number } {
    // Random angle in radians within 270° arc (from -45° to 225°)
    // -45° = -Math.PI/4, 225° = 5*Math.PI/4
    // Total span = 270° = 3*Math.PI/2
    const startAngle = -Math.PI / 4; // -45 degrees
    const angle = startAngle + Math.random() * (3 * Math.PI / 2);
    // Random speed
    const speed = VELOCITY_MIN + Math.random() * (VELOCITY_MAX - VELOCITY_MIN);
    
    return {
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
    };
}

/**
 * LillyEmitter: Spawns lily particles from a point while isEmitting is true
 */
export function LillyEmitter({ isEmitting, origin, src, particleWidth }: LillyEmitterProps) {
    const [particles, setParticles] = useState<LillyParticle[]>([]);
    const particleIdCounter = useRef(0);
    const spawnIntervalRef = useRef<number | null>(null);
    const cleanupIntervalRef = useRef<number | null>(null);

    // Spawn particles while emitting
    useEffect(() => {
        if (!isEmitting || !origin) {
            // Clear spawn interval when not emitting
            if (spawnIntervalRef.current !== null) {
                window.clearInterval(spawnIntervalRef.current);
                spawnIntervalRef.current = null;
            }
            return;
        }

        // Spawn a new particle immediately
        const spawnParticle = () => {
            setParticles((prev) => {
                // Limit max particles
                if (prev.length >= MAX_PARTICLES) {
                    return prev;
                }

                const { vx, vy } = generateVelocity();
                const lifetime = PARTICLE_LIFETIME_MIN + Math.random() * (PARTICLE_LIFETIME_MAX - PARTICLE_LIFETIME_MIN);
                const rotationSpeed = (Math.random() < 0.5 ? -1 : 1) * (ROTATION_SPEED_MIN + Math.random() * (ROTATION_SPEED_MAX - ROTATION_SPEED_MIN));

                // Add small jitter to origin for natural burst feel
                const jitterX = (Math.random() - 0.5) * 15;
                const jitterY = (Math.random() - 0.5) * 15;

                const newParticle: LillyParticle = {
                    id: particleIdCounter.current++,
                    startX: origin.x,// + jitterX - 200,
                    startY: origin.y,// + jitterY - 200,
                    velocityX: vx,
                    velocityY: vy,
                    rotationSpeed,
                    lifetime,
                    createdAt: performance.now(),
                };

                return [...prev, newParticle];
            });
        };

        // Spawn first particle immediately
        spawnParticle();

        // Then spawn at interval
        spawnIntervalRef.current = window.setInterval(spawnParticle, SPAWN_INTERVAL_MS);

        return () => {
            if (spawnIntervalRef.current !== null) {
                window.clearInterval(spawnIntervalRef.current);
                spawnIntervalRef.current = null;
            }
        };
    }, [isEmitting, origin]);

    // Clean up expired particles
    useEffect(() => {
        cleanupIntervalRef.current = window.setInterval(() => {
            const now = performance.now();
            setParticles((prev) => prev.filter((p) => now - p.createdAt < p.lifetime + 100));
        }, 100);

        return () => {
            if (cleanupIntervalRef.current !== null) {
                window.clearInterval(cleanupIntervalRef.current);
                cleanupIntervalRef.current = null;
            }
        };
    }, []);

    if (!origin) {
        return null;
    }

    return (
        <EmitterOverlay>
            <AnimatePresence>
                {particles.map((particle) => {
                    // Calculate end position based on velocity and lifetime
                    const endX = particle.startX + (particle.velocityX * particle.lifetime) / 1000;
                    const endY = particle.startY + (particle.velocityY * particle.lifetime) / 1000;
                    const endRotation = (particle.rotationSpeed * particle.lifetime) / 1000;

                    // Offset by half particle size to center on origin point
                const offsetX = particleWidth / 2;
                const offsetY = 0;

                return (
                        <ParticleImage
                            key={particle.id}
                            src={src}
                            alt=""
                            $width={particleWidth}
                            initial={{
                                x: particle.startX - offsetX,
                                y: particle.startY - offsetY,
                                rotate: 0,
                                opacity: 0,
                            }}
                            animate={{
                                x: endX - offsetX,
                                y: endY - offsetY,
                                rotate: endRotation,
                                opacity: [0, 1, 1, 0],
                                scale: [0.8, 1, 1, 0.9],
                            }}
                            transition={{
                                duration: particle.lifetime / 1000,
                                ease: [0.16, 1, 0.3, 1], // Smooth ease-out for floaty feel
                                opacity: {
                                    times: [0, 0.15, 0.75, 1],
                                    duration: particle.lifetime / 1000,
                                    ease: 'easeOut',
                                },
                                scale: {
                                    times: [0, 0.2, 0.8, 1],
                                    duration: particle.lifetime / 1000,
                                    ease: 'easeOut',
                                },
                            }}
                        />
                    );
                })}
            </AnimatePresence>
        </EmitterOverlay>
    );
}

const EmitterOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 9999;
    pointer-events: none;
    overflow: hidden;
`;

const ParticleImage = styled(motion.img)<{ $width: number }>`
    position: absolute;
    top: 0;
    left: 0;
    width: ${(p) => p.$width}px;
    height: auto;
    pointer-events: none;
    user-select: none;
    transform-origin: center center;
    will-change: transform, opacity;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.25));
`;

